/**
 * Which fields the last save was refused over, drawn on those fields rather than only in a
 * banner at the top.
 *
 * A page that saves six settings at once and answers "the value is out of range" has not
 * said which value. The API does say: every validation refusal carries the field's name,
 * and every settings screen gives a control the id the API calls that field. So the page
 * wraps its form in `FieldErrors`, and the control with that id turns red, the row it sits
 * in says why under its explanation, and the page scrolls to it and puts the cursor there.
 * No row is wired up by hand, which is the point: a new setting is covered the moment it
 * follows the naming every other one does.
 *
 * When nothing on the page has that id — the field is hidden, or it is not a field at all —
 * nothing is marked and the banner is still the whole message, so it never goes missing.
 *
 * Its own file, apart from `FieldErrors`, so the component's module exports only a component
 * (`react/only-export-components`).
 */

import { createContext, useContext, type ChangeEvent } from 'react';

export interface FieldProblem {
  /** The API's name for the field, or null when the refusal was not about one. */
  field: string | null;
  /** What the banner says: the reason itself, or — with several fields — that there are several. */
  message: string;
  /**
   * Every field found wrong at once, each with its own reason — a form checked before it was
   * sent, where three boxes were empty. `field` is the first of them, and `message` the
   * sentence that counts them. Absent for a refusal from the server, which names one.
   */
  fields?: readonly { field: string; message: string }[];
}

export interface Mark {
  /** The id of the control to mark: the field's name behind the page's prefix. */
  id: string;
  message: string;
}

export interface Marked {
  marks: readonly Mark[];
  /** Somebody edited the control with this id; its mark no longer holds. */
  onEdit?: (id: string) => void;
}

export const MarkedContext = createContext<Marked | null>(null);

/** The id of the sentence that says what is wrong, which the control points at. */
export function errorIdOf(id: string): string {
  return `${id}-error`;
}

/** Each field the problem is about, with the words to write under it. */
export function marksOf(problem: FieldProblem | null, prefix = ''): Mark[] {
  if (problem === null) return [];

  if (problem.fields !== undefined && problem.fields.length > 0) {
    return problem.fields.map(({ field, message }) => ({ id: `${prefix}${field}`, message }));
  }

  return problem.field === null ? [] : [{ id: `${prefix}${problem.field}`, message: problem.message }];
}

/** What is wrong with the field behind whichever of these control ids is marked, or null. */
export function useFieldError(ids: readonly string[]): Mark | null {
  const marked = useContext(MarkedContext);

  return marked?.marks.find((mark) => ids.includes(mark.id)) ?? null;
}

/**
 * The attributes a control spreads to be marked: `aria-invalid`, which is what turns its
 * border red and what a screen reader announces, the sentence it is described by, and its
 * own `onChange` wrapped so the first edit clears its mark — its own, not the others'.
 */
export function useInvalid<T extends Element>(
  id: string | undefined,
  describedBy: string | undefined,
  onChange: ((event: ChangeEvent<T>) => void) | undefined,
) {
  const marked = useContext(MarkedContext);
  const mark = id === undefined ? undefined : marked?.marks.find((m) => m.id === id);

  if (marked === null || id === undefined || mark === undefined) {
    return { 'aria-describedby': describedBy, onChange };
  }

  return {
    'aria-invalid': true,
    'aria-describedby': [describedBy, errorIdOf(id)].filter(Boolean).join(' '),
    onChange: (event: ChangeEvent<T>) => {
      marked.onEdit?.(id);
      onChange?.(event);
    },
  };
}
